(ns manage.ad-variant.details-ocp.details
  (:require ["@mui/material/Accordion$default" :as Accordion]
            ["@mui/material/AccordionDetails$default" :as AccordionDetails]
            ["@mui/material/AccordionSummary$default" :as AccordionSummary]
            ["@mui/material/Alert$default" :as Alert]
            ["@mui/material/Chip$default" :as Chip]
            ["@mui/material/IconButton$default" :as IconButton]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/SvgIcon$default" :as SvgIcon]
            ["@mui/material/ToggleButton$default" :as ToggleButton]
            ["@mui/material/ToggleButtonGroup$default" :as ToggleButtonGroup]
            ["@mui/material/Tooltip$default" :as Tooltip]
            ["@mui/material/Typography$default" :as Typography]
            ["src/svg/inform/error-diamond-fill.svg$default" :as ErrorIcon]
            ["src/svg/wpp/arrows/chevron-down.svg$default" :as ChevronDown]
            ["src/svg/wpp/content-actions/external-link.svg$default"
             :as ExternalLink]
            [clojure.string :as str]
            [common.queries :refer [use-query]]
            [common.util :refer [format-iso-string]]
            [manage.ad-variant.widgets :as variant-widgets]
            [manage.adset-decision-tree-page.tagging-and-trackers.tagging.db
             :refer [params->url]]
            [manage.adset-decision-tree-page.view.navigation-panel
             :refer [use-ad-group-summary]]
            [manage.adset-decision-tree-page.view.table-util :as tu]
            [manage.adset-decision-tree-page.view.table.base
             :refer [use-table-query-fn use-activation-ad-variant-dg-filter-item]]
            [manage.adset-integration-meta-settings.db :as meta-settings-db]
            [manage.adset.hooks :refer [use-channel use-is-social-adset?]]
            [re-frame.core :as rf]
            [reitit.frontend.easy :as rfe]
            [uix.core :as uix :refer [$ defui]]
            [uix.re-frame :refer [use-subscribe]]))

(defn- get-landing-urls [variant]
  (or
   (when-let [url (or (get-in variant [:components :urn:meta:click-through :value])
                      (get-in variant [:components :urn:tiktok:click-through :value]))]
     [{:url url
       :identifier "Click through"}])
   (when-let [url (get-in variant [:components :urn:vast:click-through :value])]
     [{:url url
       :identifier "Click through"}])
   (->> (get-in variant [:components
                         :urn:lemonpi:artefact:display:content+template
                         :value :content])
        (keep (fn [[identifier {:keys [type value]}]]
                (when (= type "click")
                  {:identifier (str/capitalize
                                (str/replace (name identifier) #"_" " "))
                   :url value}))))))

(defn get-tagging-and-trackers
  [{{:keys [ad-variants]} :assigned-node-data
    {variant-uuid :uuid} :variant}]
  (let [{:keys [trackers click-through-url-tagging]}
        (some #(when (= variant-uuid (:uuid %)) %) ad-variants)]
    {:click-tracker {:url (->> trackers (filter #(= (:type %) "click")) first :value)}
     :tagging {:url (when-let [tagging-parts (seq (map (juxt :name (comp first :values))
                                                       click-through-url-tagging))]
                      (params->url tagging-parts))}
     :impression-trackers
     (let [impression-trackers (filter #(= (:type %) "impression") trackers)]
       (if (< 1 (count impression-trackers))
         {:urls (map-indexed (fn [i {:keys [value]}]
                               {:identifier (str (inc i))
                                :url value})
                             impression-trackers)}
         {:url (-> impression-trackers first :value)}))}))

(defui url-field [{:keys [url urls]}]
  (let [[selected set-selected] (uix/use-state (-> urls first :identifier))
        selected-url (or url
                         (->> urls
                              (filter #(= (:identifier %) selected))
                              first
                              :url))]
    ($ Stack {:sx #js {:border "1px solid"
                       :borderColor "grey.40"
                       :borderRadius 2
                       :p 2}}
       (when urls
         ($ ToggleButtonGroup
            {:value selected
             :exclusive true
             :color "primary.main"
             :onChange (fn [_ value] (when value (set-selected value)))
             :sx #js {:mt -1
                      :ml "auto"
                      "& .MuiToggleButton-root" #js {:border "none"
                                                     :textTransform "none"}
                      "& .MuiToggleButton-root.Mui-selected" #js {:bgcolor "transparent"}
                      "& .MuiToggleButton-root.Mui-selected:hover" #js {:bgcolor "transparent"}
                      "& .MuiToggleButton-root:hover" #js {:bgcolor "transparent"}}}
            (for [{:keys [identifier]} urls]
              ($ ToggleButton {:key identifier
                               :value identifier
                               :sx #js {:p 0.5}}
                 identifier))))
       ($ Typography {:sx #js {:wordBreak "break-all"
                               :whiteSpace "pre-wrap"}}
          (or selected-url "")))))

(defui section [{:keys [label children]}]
  ($ Stack {:gap 1}
     ($ Typography {:component "label" :fontWeight "600" :color "text.secondary"} label)
     children))

(defui strategy-labels [{:keys [targeting-values]}]
  (when (seq targeting-values)
    ($ section {:label "Strategy labels"}
       ($ Stack {:direction "row" :spacing 1 :flexWrap "wrap"}
          (map-indexed
           (fn [idx {:keys [value name]}]
             ($ Tooltip {:placement :bottom
                         :key idx
                         :title name}
                ($ Chip {:variant "outlined"
                         :size "medium"
                         :label (str/join "/ " value)
                         :sx #js {:borderRadius 8
                                  :borderColor "grey.40"
                                  :color "text.primary"
                                  :bgcolor "background.paper"}})))
           targeting-values)))))

(defui scheduled-section [{:keys [scheduling]}]
  (when scheduling
    ($ section {:label "Scheduled"}
       ($ variant-widgets/schedule-summary scheduling))))

(defui tracking-accordion-item [{:keys [id title expanded set-expanded] :as props}]
  ($ Accordion {:expanded (= expanded id)
                :onChange (fn [] (set-expanded (when-not (= expanded id) id)))
                :sx #js {:borderRadius 2
                         :p 1
                         :bgcolor "grey.90"}}
     ($ AccordionSummary {:expandIcon ($ SvgIcon {:component ChevronDown
                                                  :inheritViewBox true
                                                  :sx #js {:p 0.25}})}
        ($ Typography {:fontWeight "600"
                       :color (when-not (= expanded id)
                                "text.secondary")}
           title))
     ($ AccordionDetails
        ($ url-field props))))

(defui cm360-accordion-item [{:keys [id expanded set-expanded cm360]}]
  (let [{:keys [success trackers resources]} cm360]
    ($ Accordion {:expanded (= expanded id)
                  :onChange (fn [] (set-expanded (when-not (= expanded id) id)))
                  :sx #js {:borderRadius 2 :p 1 :bgcolor "grey.90"}}
       ($ AccordionSummary {:expandIcon ($ SvgIcon {:component ChevronDown
                                                    :inheritViewBox true
                                                    :sx #js {:p 0.25}})}
          ($ Stack {:direction "row" :spacing 1 :alignItems "center"}
             ($ Typography {:fontWeight "600"
                            :color (when-not (= expanded id) "text.secondary")}
                "CM360 tracker")
             (when (false? success)
               ($ Chip {:label "Failed" :color "error" :size "small"}))))
       ($ AccordionDetails
          ($ Stack {:spacing 1}
             (when (:click trackers)
               ($ url-field {:url (:click trackers)}))
             ($ section {:label "Placement name"}
                ($ url-field {:url (:placement-name resources)})))))))

(defui url-tracking-section [{:keys [variant strategy-row] :as props}]
  (let [landing-urls (get-landing-urls variant)
        {:keys [click-tracker impression-trackers tagging]} (get-tagging-and-trackers props)
        cm360 (get-in strategy-row [:tracker-data :cm360])
        [expanded set-expanded] (uix/use-state "landing-url")]
    ($ section {:label (if strategy-row "URL & Tracking" "Landing url(s)")}
       (if strategy-row
         ($ :<>
            (for [{:keys [url urls id] :as item}
                  [{:id "landing-url" :title "Landing url" :urls landing-urls}
                   (merge {:id "utm-tagging" :title "UTM Tagging"} tagging)
                   (merge {:id "click-tracker" :title "Click tracker"} click-tracker)
                   (merge {:id "impression-tracker" :title "Impression tracker"}
                          impression-trackers)]
                  :when (or url (seq urls))]
              ($ tracking-accordion-item (assoc item
                                                :key id
                                                :expanded expanded
                                                :set-expanded set-expanded)))
            (when cm360
              ($ cm360-accordion-item {:id "cm360-tracker"
                                       :expanded expanded
                                       :set-expanded set-expanded
                                       :cm360 cm360})))
         ($ url-field {:urls landing-urls})))))

(defui social-sections [strategy-row]
  (let [channel (use-channel)
        {account-summary :summary} (meta-settings-db/use-settings-details)
        group-id (tu/get-ad-group-id channel strategy-row)
        ad-group-summary (use-ad-group-summary group-id)
        platform-name (case channel
                        "meta" "Meta"
                        "tiktok" "TikTok"
                        nil)]
    ($ :<>
       ($ section {:label (str platform-name " Ad ID")}
          (tu/get-ad-id channel strategy-row))
       ($ section {:label (:label ad-group-summary)}
          (:description ad-group-summary))
       ($ section {:label (str platform-name " account")}
          account-summary))))

(defui targeting-status [{:keys [ad-variant-uuid]}]
  (let [query-fn (use-table-query-fn)
        url-context (use-subscribe [:url-context])
        dg-filter-item (use-activation-ad-variant-dg-filter-item ad-variant-uuid)
        {{:keys [total data]} :data} (use-query
                                      (query-fn
                                       {:pagination-model #js {:page 0, :pageSize 100}
                                        :sort-model #js []
                                        :filter-model #js {:items #js [dg-filter-item]}}))
        is-all-removed (every? (comp #{"removed"} :status :data) data)]
    ($ Stack {:direction "row" :justifyContent "space-between"}
       ($ section {:label "Targeting status"}
          (when (some? total)
            (str (cond
                   (and (< 0 total) is-all-removed) "Was assigned"
                   (< 0 total) "Assigned"
                   :else "Not assigned")
                 " to the Decision Tree")))
       ($ IconButton
          {:href (rfe/href :adset/ad-variants
                           url-context
                           {:ad-variant-filter (str ad-variant-uuid)
                            :show-removed is-all-removed}
                           "strategy")
           :onClick #(rf/dispatch [:ocp/close])}
          ($ SvgIcon {:component ExternalLink
                      :inheritViewBox true})))))

(defui variant-details [{:keys [variant strategy-row] :as props}]
  (let [is-social (use-is-social-adset?)]
    ($ Stack {:spacing 3}
       (if strategy-row
         (when is-social
           ($ social-sections strategy-row))
         ($ targeting-status variant))
       (when strategy-row
         ($ section {:label "Decision Path"}
            (tu/get-decision-path strategy-row)))
       ($ scheduled-section variant)
       ($ strategy-labels variant)
       ($ url-tracking-section props)
       (when strategy-row
         ($ section {:label "Created date"}
            (format-iso-string (:created-at strategy-row))))
       (when strategy-row
         ($ section {:label "Last updated date"}
            (format-iso-string (:updated-at strategy-row)))))))
